/**
 * DoctorSubscriptionRequired — subscribe-CTA card, rendered inside the
 * DoctorAccessGate modal overlay when a doctor's grace has expired or
 * they've never subscribed.
 *
 * Ships as a self-contained CARD (no full-viewport wrapper) so the
 * overlay container in DoctorAccessGate can position it over the dimmed
 * dashboard. Previously fought with `min-h-screen bg-gray-50` full-page
 * layout that made the dashboard-visible-behind-modal UX impossible.
 *
 * Also renders a "Sign out" affordance so a user who reaches this
 * screen by mistake (wrong account, etc.) can escape without support.
 *
 * Copy hierarchy is deliberately trial-first: the free trial is the hero and
 * the monthly price is secondary, since doctors meet this card before they
 * have seen any value from the product. The expired variant inverts that —
 * the trial is gone, so the price is the actual decision.
 */

import { useClerk } from '@clerk/clerk-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { apiClient, type DoctorSubscriptionState } from '../api';
import { Button } from './ui/button';

const FLAT_TIER_ID = 'doctor_monthly';
const PERCENT_TIER_ID = 'doctor_percent';
const MONTHLY_PRICE = 150;
const TRIAL_DAYS = 30;
const REVENUE_PERCENT = 3;

const LOCK_ICON_PATH =
  'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z';
const CALENDAR_ICON_PATH =
  'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z';

interface DoctorSubscriptionRequiredProps {
  state: DoctorSubscriptionState;
}

export function DoctorSubscriptionRequired({ state }: DoctorSubscriptionRequiredProps) {
  const [starting, setStarting] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string>(FLAT_TIER_ID);
  const { signOut } = useClerk();

  const isExpired = state.reason === 'expired';
  const percentSelected = selectedTier === PERCENT_TIER_ID;

  const handleSubscribe = async () => {
    setStarting(true);
    try {
      const { url } = await apiClient.createSubscriptionCheckout(selectedTier);
      window.location.href = url;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start checkout';
      toast.error(message);
      setStarting(false);
    }
  };

  const optionClass = (selected: boolean) =>
    `block w-full cursor-pointer text-left rounded-lg border p-4 transition-colors ${
      selected
        ? 'border-[#A66B32] bg-[#EAE0CB]/40 ring-1 ring-[#A66B32]'
        : 'border-gray-200 bg-white hover:border-[#A66B32]/50'
    }`;

  return (
    <div className="w-full max-w-lg mx-4">
      <div className="w-full bg-white rounded-lg shadow-2xl p-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#EAE0CB] mb-4">
            <svg
              className="w-8 h-8 text-[#A66B32]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isExpired ? LOCK_ICON_PATH : CALENDAR_ICON_PATH}
              />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            {isExpired
              ? 'Your ExpertMD grace period ended'
              : `Your first ${TRIAL_DAYS} days are free`}
          </h1>
          <p className="text-gray-600 mb-6">
            {isExpired
              ? 'Your dashboard is on hold. Activate your subscription to continue managing cases, sending invoices, and receiving payouts.'
              : 'Unlock the full dashboard — cases, invoicing with Stripe payouts, team members, and templated communication. No charge today, and you can cancel before the trial ends.'}
          </p>

          <fieldset className="space-y-3 mb-6 border-0 p-0 m-0">
            <legend className="sr-only">Choose your plan</legend>
            <label className={optionClass(!percentSelected)}>
              <input
                type="radio"
                name="doctor-plan"
                value={FLAT_TIER_ID}
                checked={!percentSelected}
                onChange={() => setSelectedTier(FLAT_TIER_ID)}
                className="sr-only"
              />
              {isExpired ? (
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-[#241610]">${MONTHLY_PRICE}</span>
                  <span className="text-[#3A2718]">/ month</span>
                </div>
              ) : (
                <>
                  <div className="text-2xl font-bold text-[#241610] leading-tight">
                    {TRIAL_DAYS} days free
                  </div>
                  <div className="text-sm text-[#3A2718] mt-1">
                    then ${MONTHLY_PRICE}/month — first charge on day {TRIAL_DAYS + 1}
                  </div>
                </>
              )}
              <div className="text-xs text-gray-500 mt-1">
                Flat fee — keep 100% of your invoice revenue.
              </div>
            </label>

            <label className={optionClass(percentSelected)}>
              <input
                type="radio"
                name="doctor-plan"
                value={PERCENT_TIER_ID}
                checked={percentSelected}
                onChange={() => setSelectedTier(PERCENT_TIER_ID)}
                className="sr-only"
              />
              <div className="text-2xl font-bold text-[#241610] leading-tight">
                {REVENUE_PERCENT}% of revenue
              </div>
              <div className="text-sm text-[#3A2718] mt-1">
                No monthly fee — pay {REVENUE_PERCENT}% of payments collected through ExpertMD
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Includes all payments through the platform — cards and checks — billed monthly to
                the card on file.
              </div>
            </label>
          </fieldset>

          <Button
            onClick={handleSubscribe}
            disabled={starting}
            className="w-full bg-[#3A2718] hover:bg-[#241610] text-white py-3 text-base font-medium"
          >
            {starting
              ? 'Redirecting to Stripe…'
              : percentSelected
                ? `Continue with ${REVENUE_PERCENT}% of revenue`
                : isExpired
                  ? 'Reactivate subscription'
                  : `Start ${TRIAL_DAYS}-day free trial`}
          </Button>

          <p className="text-xs text-gray-500 mt-4">
            Your Stripe Connect payouts from existing invoices are unaffected either way. You can
            cancel any time from the billing portal.
          </p>

          <div className="mt-6 pt-6 border-t border-gray-100 text-sm text-gray-500">
            Wrong account?{' '}
            <button
              onClick={() => signOut()}
              className="text-[#A66B32] hover:text-[#3A2718] font-medium"
              type="button"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
